import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { fetchAutenticat } from "../../utils/auth";

const PAGE_SIZES = [10, 25, 50];

function Seleccionarusuari({
  onUserSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedUser,
  setUsuaris,
  reloadToken,
  filters,
}) {
  const { empresaSeleccionada, esSuperUsuari, rolUsuari } = useUserContext();

  const [localText, setLocalText] = useState("");
  const filterText = filters?.filterText ?? localText;
  const setFilterText = filters?.setFilterText ?? setLocalText;

  const [localRol, setLocalRol] = useState("");
  const rolFilter = filters?.rolFilter ?? localRol;
  const setRolFilter = filters?.setRolFilter ?? setLocalRol;

  const [actiusOnly, setActiusOnly] = useState(true);
  const [empresaFilter, setEmpresaFilter] = useState("");

  const [usuaris, setUsuarisLocals] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rols, setRols] = useState([]);
  const [empreses, setEmpreses] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "NomUsuari", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR", "USUARIS");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR", "USUARIS");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR", "USUARIS");

  const roleKey = (id) => (id === null || id === undefined ? null : String(Number(id)));
  const getUserRoleKey = (u) => roleKey(u?.Rol_idRol ?? u?.idRol ?? u?.rol_idRol ?? u?.rolId ?? null);
  const empresaLabel = (em) =>
    em?.NomComercial && String(em.NomComercial).trim()
      ? em.NomComercial
      : em?.Nom && String(em.Nom).trim()
      ? em.Nom
      : `Empresa #${em?.idEmpresa}`;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchRols = async () => {
      try {
        const res = await fetch(`${API_URL}/Rols.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        setRols(data.success ? data.rols || [] : []);
      } catch (e) {
        console.error("Error carregant Rols:", e);
        setRols([]);
      }
    };
    fetchRols();
  }, []);

  useEffect(() => {
    if (!esSuperUsuari) return;
    const fetchEmp = async () => {
      try {
        const res = await fetch(`${API_URL}/Empreses.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        const list = data.success ? data.empreses || [] : [];
        setEmpreses(list);
        if (empresaSeleccionada?.IdEmpresa) {
          setEmpresaFilter(String(empresaSeleccionada.IdEmpresa));
        } else if (list[0]?.idEmpresa) {
          setEmpresaFilter(String(list[0].idEmpresa));
        }
      } catch (e) {
        console.error("Error carregant Empreses:", e);
        setEmpreses([]);
      }
    };
    fetchEmp();
  }, [esSuperUsuari, empresaSeleccionada]);

  useEffect(() => {
    const fetchUsuaris = async () => {
      const empresaId = esSuperUsuari ? Number(empresaFilter) || null : empresaSeleccionada?.IdEmpresa;
      if (!empresaId) return;
      setLoading(true);
      try {
        const res = await fetchAutenticat(`${API_URL}/Usuaris.php`, {
          method: "POST",
          body: JSON.stringify({ idEmpresa: empresaId }),
        });
        const data = await res.json();
        const list = data.success ? data.usuaris || [] : [];
        setUsuarisLocals(list);
      } catch (e) {
        console.error("Error carregant Usuaris:", e);
        setUsuarisLocals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsuaris();
  }, [empresaSeleccionada, esSuperUsuari, empresaFilter, reloadToken]);

  const rolByKey = useMemo(() => {
    const m = {};
    for (const r of rols) m[roleKey(r.idRol)] = r;
    return m;
  }, [rols]);

  useEffect(() => {
    const txt = (filterText || "").toLowerCase();
    let list = usuaris.filter((u) => {
      if (actiusOnly && Number(u.Actiu) !== 1) return false;
      if (!actiusOnly && Number(u.Actiu) !== 0) return false;
      const rKey = getUserRoleKey(u);
      if (rolFilter && rKey !== roleKey(rolFilter)) return false;
      const r = rolByKey[rKey];
      const camp = [u.NomUsuari, r?.Rol ?? "", String(r?.NivellPermisos ?? "")].filter(Boolean).join(" ").toLowerCase();
      return camp.includes(txt);
    });

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        let aVal;
        let bVal;
        if (key === "NomUsuari") {
          aVal = (a.NomUsuari || "").toLowerCase();
          bVal = (b.NomUsuari || "").toLowerCase();
        } else if (key === "RolName") {
          aVal = (rolByKey[getUserRoleKey(a)]?.Rol || "").toLowerCase();
          bVal = (rolByKey[getUserRoleKey(b)]?.Rol || "").toLowerCase();
        } else if (key === "NivellPermisos") {
          aVal = Number(rolByKey[getUserRoleKey(a)]?.NivellPermisos ?? -9999);
          bVal = Number(rolByKey[getUserRoleKey(b)]?.NivellPermisos ?? -9999);
        } else {
          aVal = "";
          bVal = "";
        }
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }

    setFiltered(list);
    setUsuaris?.(list);
    setPage(1);
  }, [filterText, rolFilter, actiusOnly, usuaris, sortConfig, rolByKey, setUsuaris]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const obrirMenu = (e, item) => {
    e.stopPropagation();
    if (menuObert === item.idUsuaris) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(item.idUsuaris);
  };

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filtered.slice(inici, inici + pageSize);
  const itemMenu = menuObert ? filtered.find((u) => u.idUsuaris === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = paginaActual - 1; i <= paginaActual + 1; i += 1) {
      if (i > 1 && i < totalPages) afegir(i);
    }
    if (totalPages > 1) afegir(totalPages);
    return pages.sort((a, b) => a - b);
  };

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (item) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === item.idUsuaris ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, item)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per usuari o rol…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Rol</span>
          <select className="dv2-select" value={rolFilter} onChange={(e) => setRolFilter(e.target.value)}>
            <option value="">Tots</option>
            {rols.map((r) => (
              <option key={r.idRol} value={r.idRol}>
                {r.Rol}
                {r.NivellPermisos != null ? ` (${r.NivellPermisos})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Mostra actius</span>
          <span className="dv2-toggle">
            <input type="checkbox" checked={actiusOnly} onChange={() => setActiusOnly((v) => !v)} />
            <span className="dv2-toggle-track" />
          </span>
        </label>
        {esSuperUsuari && (
          <label className="dv2-filter">
            <span className="dv2-filter-label">Empresa</span>
            <select className="dv2-select" value={empresaFilter} onChange={(e) => setEmpresaFilter(e.target.value)}>
              <option value="">{empresaSeleccionada?.IdEmpresa ? `(${empresaSeleccionada.IdEmpresa})` : "Selecciona"}</option>
              {empreses.map((em) => (
                <option key={em.idEmpresa} value={em.idEmpresa}>
                  {empresaLabel(em)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant usuaris…</p>
      ) : filtered.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap usuari.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((u) => {
                const r = rolByKey[getUserRoleKey(u)];
                return (
                  <article
                    key={u.idUsuaris}
                    className="dv2-record"
                    onClick={() => onUserSelect?.(u)}
                    onDoubleClick={() => pucConsultar && onConsultar?.(u)}
                  >
                    <div>
                      <p className="dv2-record-title">{u.NomUsuari || "—"}</p>
                      <p className="dv2-record-meta">{r?.Rol ?? "Sense rol"}</p>
                    </div>
                    {renderAccions(u)}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("NomUsuari", "Usuari")}
                    {th("RolName", "Rol")}
                    {th("NivellPermisos", "Nivell")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((u) => {
                    const r = rolByKey[getUserRoleKey(u)];
                    return (
                      <tr
                        key={u.idUsuaris}
                        className={selectedUser && Number(selectedUser.idUsuaris) === Number(u.idUsuaris) ? "is-selected" : ""}
                        onClick={() => onUserSelect?.(u)}
                        onDoubleClick={() => pucConsultar && onConsultar?.(u)}
                      >
                        <td>{u.NomUsuari}</td>
                        <td>{r?.Rol ?? "—"}</td>
                        <td>{r?.NivellPermisos ?? "—"}</td>
                        <td className="dv2-table-actions">{renderAccions(u)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)} aria-label="Primera pàgina">
                «
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Pàgina anterior">
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button type="button" className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`} onClick={() => setPage(n)}>
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Pàgina següent">
                ›
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage(totalPages)} aria-label="Última pàgina">
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {itemMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onConsultar?.(itemMenu); }}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onModificar?.(itemMenu); }}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => { setMenuObert(null); onEsborrar?.(itemMenu); }}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionarusuari;
